import { z } from 'zod';
import { AddressSchema } from '@/schemas/models/address';
import { ErrorResponseSchema } from '@/schemas/common/base';
import {
  latitudeValidatorCoerceNullable,
  longitudeValidatorCoerceNullable,
  idValidatorNonNegativeOptional,
} from '@/schemas/common/validators';

/**
 * Address ID parameter schema for route params
 * Shared across address endpoints that require an address ID
 */
export const AddressIdParamSchema = z.object({
  id: z.coerce.number().int().positive(),
});

/**
 * PUT /api/addresses/[id] request schema
 * Updates an existing address for the authenticated user
 * All fields are optional - only provided fields will be updated
 */
export const EditAddressRequestSchema = z.object({
  /** Full name associated with address (optional) */
  fullName: z.string().max(255).optional(),
  /** Shipping name (optional, can differ from fullName) */
  shippingName: z.string().max(255).optional(),
  /** Country ID */
  countryId: idValidatorNonNegativeOptional,
  /** City/locality */
  locality: z.string().min(2, 'City must be at least 2 characters').max(255).optional(),
  /** State/region/province */
  region: z.string().min(2, 'State/Region must be at least 2 characters').max(255).optional(),
  /** Address line 1 (street address) */
  line1: z.string().min(4, 'Address line 1 must be at least 4 characters').max(255).optional(),
  /** Address line 2 (apartment, suite, etc.) - optional */
  line2: z.string().max(255).optional(),
  /** Postal/ZIP code */
  zipCode: z.string().min(4, 'ZIP/Postal code must be at least 4 characters').max(20).optional(),
  /**
   * Coordinates (optional). `null` clears them. They were coerce-only, and
   * z.coerce.number() reads a JSON null as 0, so clearing a point saved the
   * address at 0,0 in the Gulf of Guinea.
   */
  longitude: longitudeValidatorCoerceNullable.optional(),
  latitude: latitudeValidatorCoerceNullable.optional(),
  /** Whether this should be set as the default address (optional) */
  isDefault: z.union([z.boolean(), z.number().int().min(0).max(1)]).optional(),
});

/**
 * PUT /api/addresses/[id] response schema
 * Returns the updated address on success
 */
export const EditAddressResponseSchema = z.discriminatedUnion('success', [
  z.object({
    success: z.literal(true),
    address: AddressSchema,
  }),
  ErrorResponseSchema,
]);

export type AddressIdParam = z.infer<typeof AddressIdParamSchema>;
export type EditAddressRequest = z.infer<typeof EditAddressRequestSchema>;
export type EditAddressResponse = z.infer<typeof EditAddressResponseSchema>;
